/**
 * EntityViewers
 *
 * Displays avatars of other users currently viewing the same entity.
 * Uses MUI AvatarGroup with tooltips showing viewer names.
 */

import { Avatar, AvatarGroup, Chip, Tooltip, useTheme, type Theme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { neutralGrey } from '@/styles/theme';
import type { EntityViewer } from '@/hooks/useEntitySync';

interface EntityViewersProps {
  viewers: EntityViewer[];
}

/** The name shown for a viewer (#3409): the server's display name, else what it sent before. */
export function viewerLabel(viewer: EntityViewer): string {
  return viewer.displayName || [viewer.firstName, viewer.lastName].filter(Boolean).join(' ') || viewer.firstName;
}

/** "TB" for Teemu Brusila; a single letter when only one name or an email is known. */
export function viewerInitials(viewer: EntityViewer): string {
  const first = viewer.firstName.trim().charAt(0);
  const last = (viewer.lastName ?? '').trim().charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || viewerLabel(viewer).charAt(0).toUpperCase();
}

function getColor(name: string, theme: Theme): string {
  // Brand-derived categorical palette: distinct hues for hashing viewer
  // avatars while staying on-brand.
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.dark,
    theme.palette.success.dark,
    theme.palette.warning.dark,
    neutralGrey,
    theme.palette.text.disabled,
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length] ?? neutralGrey;
}

export function EntityViewers({ viewers }: EntityViewersProps) {
  const { t: tc } = useTranslation('common');
  const theme = useTheme();

  if (viewers.length === 0) return null;

  const names = viewers.map(viewerLabel).join(', ');

  return (
    <Tooltip title={tc('realtime.viewingNow', { names })}>
      <Chip
        size="small"
        icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
        label={
          <AvatarGroup
            max={3}
            sx={{
              '& .MuiAvatar-root': {
                width: 22,
                height: 22,
                fontSize: '0.7rem',
                border: '1.5px solid',
                borderColor: 'background.paper',
              },
            }}
          >
            {viewers.map((viewer) => (
              <Avatar
                key={viewer.userId}
                sx={{
                  bgcolor: getColor(viewerLabel(viewer), theme),
                  width: 22,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              >
                {viewerInitials(viewer)}
              </Avatar>
            ))}
          </AvatarGroup>
        }
        variant="outlined"
        sx={{
          height: 30,
          borderColor: 'primary.light',
          '& .MuiChip-label': { px: 0.5 },
        }}
      />
    </Tooltip>
  );
}
